import {
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Box
} from '@material-ui/core';
import { HighlightOff, SettingsBackupRestore } from '@material-ui/icons';
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../../services/api';
import MuiAlert, { AlertProps } from '@material-ui/lab/Alert';
import { Button } from '@components/Button';
import { Pagination } from '@material-ui/lab';
import { ContainerButtonsTop, DivSearch } from './styles';
import { NavLink } from '@components/NavLink';

interface ITab {
  cod_ghe: string;
  cod_obra: string;
  id_cad_ghe: number;
  nome_ghe: string;
  [key: string]: any;
}

const Alert = React.forwardRef<HTMLDivElement, AlertProps>(function Alert(
  props,
  ref
) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const PER_PAGE = 30;

export const Ghe = () => {
  const [data, setData] = useState<ITab[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [searchValue, setSearchValue] = useState('');
  const [dialogDelete, setDialogDelete] = useState(false);
  const [edit, setEdit] = useState<ITab | null>(null);
  const [snack, setSnack] = useState(false);
  const [message, setMessage] = useState('');
  const [severity, setSeverity] = useState<'success' | 'error' | 'info' | 'warning'>('success');
  const [totalRecords, setTotalRecords] = useState(0);

  // Carrega os dados da API
  const fetchData = async (page: number, search: string = '') => {
    try {
      setLoading(true);
      const params = {
        page,
        ...(search && { search }) // Adiciona search apenas se não estiver vazio
      };

      const response = await api.get(`ravim/ghe`, { params });
      
      setData(response.data.results);
      setTotalPages(response.data.total_pages);
      setTotalRecords(response.data.total_records || 0);
      
    } catch (error) {
      console.error('Erro ao carregar:', error);
      setSnack(true);
      setMessage('Erro ao carregar dados');
      setSeverity('error');
    } finally {
      setLoading(false);
    }
  };

  // Atualiza os dados quando a página ou pesquisa muda
  useEffect(() => {
    fetchData(currentPage, searchValue);
  }, [currentPage, searchValue]);

  const handlePageChange = (event: unknown, newPage: number) => {
    setCurrentPage(newPage);
    window.scrollTo(0, 0);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
    setCurrentPage(1); // Reset para a primeira página ao pesquisar
  };

  const handleClearSearch = () => {
    setSearchValue('');
    setCurrentPage(1);
  };

  const deleteContract = (gheItem: ITab) => {
    setEdit(gheItem);
    setDialogDelete(true);
  };

  const closeDialog = () => {
    setDialogDelete(false);
    setEdit(null);
  };

  const deleteContrato = async () => {
    if (!edit) return;
    
    try {
      const res = await api.delete(`/ravim/exclu-ghe`, { 
        data: { cod_ghe: edit.cod_ghe } 
      });
      
      if (res.status === 200) {
        setSnack(true);
        setMessage(res.data.message);
        setSeverity('success');
        setDialogDelete(false);
        // Recarrega os dados após exclusão
        fetchData(currentPage, searchValue);
      }
    } catch (err: any) {
      setSnack(true);
      setMessage(err.response?.data?.message || 'Erro ao excluir');
      setSeverity('error');
    }
  };

  const handleSnackClose = () => {
    setSnack(false);
  };

  return (
    <Container>
      <ContainerButtonsTop>
        <DivSearch>
          <TextField
            label="Pesquisar por Código ou Nome GHE"
            type="text"
            variant="outlined"
            value={searchValue}
            onChange={handleSearchChange}
            fullWidth
          />
          {searchValue && (
            <Button
              variantstyle="SECUNDARY"
              icon={<HighlightOff fontSize="medium" />}
              onClick={handleClearSearch}
              style={{ marginLeft: '8px' }}
            />
          )}
        </DivSearch>
        <NavLink to="/cadastro-ghe">
          <Button title="Cadastro de GHE" />
        </NavLink>
      </ContainerButtonsTop>

      <Typography variant="h4" align="center" style={{ marginBottom: 20, fontWeight: 'bold' }}>
        Gerenciamento de GHE
      </Typography>
      
      <Typography variant="h6" align="left" style={{ marginBottom: 16 }}>
        {searchValue 
          ? `Pesquisando por: "${searchValue}"`
          : `Total de registros: ${totalRecords}`}
      </Typography>

      <Box display="flex" justifyContent="center" marginBottom={2}>
        <Pagination
          count={totalPages}
          page={currentPage}
          onChange={handlePageChange}
          color="primary"
          size="large"
          showFirstButton
          showLastButton
        />
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell><strong>Código Obra</strong></TableCell>
              <TableCell><strong>Código GHE</strong></TableCell>
              <TableCell><strong>Nome GHE</strong></TableCell>
              <TableCell><strong>Ações</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
        <TableRow>
          <TableCell colSpan={5} align="center" style={{ padding: '40px 0' }}>
            <CircularProgress size={40} />
            <Typography variant="body1" style={{ marginTop: 16 }}>
              Carregando GHEs...
            </Typography>
          </TableCell>
        </TableRow>
            ) : data.length > 0 ? (
              data.map((item) => (
                <TableRow key={item.id_cad_ghe}>
                  <TableCell>{item.cod_obra || '-'}</TableCell>
                  <TableCell>{item.cod_ghe || '-'}</TableCell>
                  <TableCell>{item.nome_ghe || '-'}</TableCell>
                  <TableCell>
                    <Box display="flex">
                      <Link to={{ pathname: '/edit-ghe', state: item }}>
                        <IconButton aria-label="editar">
                          <SettingsBackupRestore color="primary" />
                        </IconButton>
                      </Link>
                      <IconButton 
                        aria-label="deletar"
                        onClick={() => deleteContract(item)}
                      >
                        <HighlightOff color="error" />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  {searchValue 
                    ? `Nenhum GHE encontrado para "${searchValue}"`
                    : "Nenhum registro encontrado"}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box display="flex" justifyContent="center" marginTop={2}>
        <Pagination
          count={totalPages}
          page={currentPage}
          onChange={handlePageChange}
          color="primary"
          size="large"
          showFirstButton
          showLastButton
        />
      </Box>

      <Dialog open={dialogDelete} onClose={closeDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Confirmar exclusão</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            Você está prestes a excluir o seguinte GHE:
          </Typography>
          <Typography><strong>Nome:</strong> {edit?.nome_ghe || '-'}</Typography>
          <Typography><strong>Código:</strong> {edit?.cod_ghe || '-'}</Typography>
          <Typography color="error" style={{ marginTop: 16 }}>
            Esta ação não pode ser desfeita.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} title="Cancelar" variantstyle="SECUNDARY" />
          <Button 
            onClick={deleteContrato} 
            title="Confirmar Exclusão" 
            variantstyle="RED" 
            autoFocus
          />
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack}
        autoHideDuration={6000}
        onClose={handleSnackClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={handleSnackClose} severity={severity}>
          {message}
        </Alert>
      </Snackbar>
    </Container>
  );
};